/**
 * Причины смерти по региону ВОЗ и возрастной полосе: доли внутри полосы.
 *
 * Провенанс (честно): это ПРИБЛИЖЁННЫЕ доли, составленные по мотивам
 * сводных таблиц WHO Global Health Estimates 2021 «ведущие причины смерти
 * по регионам ВОЗ и возрастным группам»
 * (https://www.who.int/data/gho/data/themes/mortality-and-global-health-estimates/ghe-leading-causes-of-death),
 * без автоматической выгрузки и без машинной сверки — записаны 2026-10-22
 * по памяти о порядке величин и округлены до сотых. Укрупнённые категории
 * GHE сведены к 13 причинам игры, доли нормированы так, чтобы сумма по
 * полосе была 1 ± 0.01. Тесты проверяют только структуру и суммы, не сами
 * значения. Таблица ПОДЛЕЖИТ СВЕРКЕ с выгрузкой GHE: калибровочный отчёт
 * (Task 14) не должен считать её эталоном при сравнении распределения
 * причин — расхождение с GHE может быть и ошибкой таблицы, и ошибкой движка.
 *
 * Это региональные средние: разница между странами и полами учитывается
 * движком через веса (ВИЧ, хроника, материнская смертность только у
 * женщин, суицид только с 13 лет, neonatal только в первый год).
 *
 * Правила таблицы:
 * - `0-4` — только neonatal / infection / injury / other;
 * - `suicide` отсутствует в `0-4` и `5-14`;
 * - `maternal` только в `15-29` и `30-49` (движок применяет лишь к `sex='f'`).
 *
 * Не GBD/IHME: лицензия WHO (CC BY-NC-SA 3.0 IGO для данных GHO) указана
 * в ATTRIBUTION.md.
 */
import type { WhoRegion } from './schema';

export const CAUSE_IDS = [
  'neonatal',
  'infection',
  'maternal',
  'injury',
  'traffic',
  'violence',
  'suicide',
  'cardio',
  'cancer',
  'respiratory',
  'diabetes',
  'dementia',
  'other',
] as const;

export type CauseId = (typeof CAUSE_IDS)[number];

export const AGE_BANDS = ['0-4', '5-14', '15-29', '30-49', '50-69', '70+'] as const;

export type AgeBand = (typeof AGE_BANDS)[number];

/** Тяжёлые причины: без описания способа, всегда с блоком помощи. */
export const HEAVY_CAUSES: ReadonlySet<CauseId> = new Set<CauseId>(['violence', 'suicide']);

export type CauseShares = Partial<Record<CauseId, number>>;

export const CAUSE_SHARES: Record<WhoRegion, Record<AgeBand, CauseShares>> = {
  AFR: {
    '0-4': { neonatal: 0.40, infection: 0.45, injury: 0.05, other: 0.10 },
    '5-14': { infection: 0.50, injury: 0.15, traffic: 0.08, violence: 0.03, cardio: 0.03, cancer: 0.04, respiratory: 0.03, diabetes: 0.01, other: 0.13 },
    '15-29': { infection: 0.32, maternal: 0.10, injury: 0.12, traffic: 0.12, violence: 0.08, suicide: 0.05, cardio: 0.05, cancer: 0.04, respiratory: 0.02, diabetes: 0.01, other: 0.09 },
    '30-49': { infection: 0.33, maternal: 0.05, injury: 0.07, traffic: 0.08, violence: 0.05, suicide: 0.03, cardio: 0.14, cancer: 0.09, respiratory: 0.03, diabetes: 0.03, other: 0.10 },
    '50-69': { infection: 0.18, injury: 0.03, traffic: 0.03, violence: 0.01, suicide: 0.01, cardio: 0.30, cancer: 0.17, respiratory: 0.06, diabetes: 0.06, dementia: 0.01, other: 0.14 },
    '70+': { infection: 0.10, injury: 0.02, traffic: 0.01, violence: 0.01, suicide: 0.01, cardio: 0.40, cancer: 0.12, respiratory: 0.09, diabetes: 0.06, dementia: 0.05, other: 0.13 },
  },
  AMR: {
    '0-4': { neonatal: 0.50, infection: 0.25, injury: 0.07, other: 0.18 },
    '5-14': { infection: 0.12, injury: 0.20, traffic: 0.14, violence: 0.08, cardio: 0.05, cancer: 0.15, respiratory: 0.04, diabetes: 0.01, other: 0.21 },
    '15-29': { infection: 0.05, maternal: 0.02, injury: 0.12, traffic: 0.17, violence: 0.28, suicide: 0.10, cardio: 0.07, cancer: 0.07, respiratory: 0.02, diabetes: 0.01, other: 0.09 },
    '30-49': { infection: 0.06, maternal: 0.01, injury: 0.08, traffic: 0.08, violence: 0.14, suicide: 0.06, cardio: 0.20, cancer: 0.17, respiratory: 0.03, diabetes: 0.04, other: 0.13 },
    '50-69': { infection: 0.05, injury: 0.03, traffic: 0.02, violence: 0.02, suicide: 0.01, cardio: 0.30, cancer: 0.27, respiratory: 0.07, diabetes: 0.07, dementia: 0.01, other: 0.15 },
    '70+': { infection: 0.05, injury: 0.02, traffic: 0.01, violence: 0.01, suicide: 0.01, cardio: 0.34, cancer: 0.18, respiratory: 0.12, diabetes: 0.06, dementia: 0.09, other: 0.11 },
  },
  SEAR: {
    '0-4': { neonatal: 0.55, infection: 0.28, injury: 0.05, other: 0.12 },
    '5-14': { infection: 0.30, injury: 0.22, traffic: 0.10, violence: 0.02, cardio: 0.05, cancer: 0.08, respiratory: 0.04, diabetes: 0.01, other: 0.18 },
    '15-29': { infection: 0.14, maternal: 0.05, injury: 0.15, traffic: 0.16, violence: 0.04, suicide: 0.14, cardio: 0.09, cancer: 0.05, respiratory: 0.03, diabetes: 0.01, other: 0.14 },
    '30-49': { infection: 0.14, maternal: 0.02, injury: 0.08, traffic: 0.09, violence: 0.02, suicide: 0.07, cardio: 0.22, cancer: 0.11, respiratory: 0.05, diabetes: 0.04, other: 0.16 },
    '50-69': { infection: 0.09, injury: 0.03, traffic: 0.02, violence: 0.01, suicide: 0.02, cardio: 0.36, cancer: 0.16, respiratory: 0.10, diabetes: 0.06, dementia: 0.01, other: 0.14 },
    '70+': { infection: 0.07, injury: 0.02, traffic: 0.01, violence: 0.01, suicide: 0.01, cardio: 0.40, cancer: 0.10, respiratory: 0.16, diabetes: 0.05, dementia: 0.05, other: 0.12 },
  },
  EUR: {
    '0-4': { neonatal: 0.55, infection: 0.15, injury: 0.08, other: 0.22 },
    '5-14': { infection: 0.06, injury: 0.22, traffic: 0.14, violence: 0.03, cardio: 0.06, cancer: 0.22, respiratory: 0.03, diabetes: 0.01, other: 0.23 },
    '15-29': { infection: 0.05, maternal: 0.01, injury: 0.18, traffic: 0.20, violence: 0.05, suicide: 0.20, cardio: 0.09, cancer: 0.09, respiratory: 0.02, diabetes: 0.01, other: 0.10 },
    '30-49': { infection: 0.06, maternal: 0.01, injury: 0.12, traffic: 0.08, violence: 0.03, suicide: 0.12, cardio: 0.25, cancer: 0.18, respiratory: 0.03, diabetes: 0.02, other: 0.10 },
    '50-69': { infection: 0.03, injury: 0.04, traffic: 0.02, violence: 0.01, suicide: 0.03, cardio: 0.36, cancer: 0.33, respiratory: 0.05, diabetes: 0.03, dementia: 0.01, other: 0.09 },
    '70+': { infection: 0.03, injury: 0.02, traffic: 0.01, violence: 0.01, suicide: 0.01, cardio: 0.45, cancer: 0.20, respiratory: 0.07, diabetes: 0.03, dementia: 0.10, other: 0.07 },
  },
  EMR: {
    '0-4': { neonatal: 0.48, infection: 0.32, injury: 0.06, other: 0.14 },
    '5-14': { infection: 0.22, injury: 0.20, traffic: 0.14, violence: 0.07, cardio: 0.05, cancer: 0.10, respiratory: 0.03, diabetes: 0.01, other: 0.18 },
    '15-29': { infection: 0.08, maternal: 0.05, injury: 0.14, traffic: 0.20, violence: 0.16, suicide: 0.05, cardio: 0.10, cancer: 0.06, respiratory: 0.02, diabetes: 0.01, other: 0.13 },
    '30-49': { infection: 0.07, maternal: 0.02, injury: 0.08, traffic: 0.11, violence: 0.10, suicide: 0.03, cardio: 0.26, cancer: 0.13, respiratory: 0.03, diabetes: 0.04, other: 0.13 },
    '50-69': { infection: 0.05, injury: 0.03, traffic: 0.03, violence: 0.02, suicide: 0.01, cardio: 0.42, cancer: 0.20, respiratory: 0.05, diabetes: 0.06, dementia: 0.01, other: 0.12 },
    '70+': { infection: 0.05, injury: 0.02, traffic: 0.01, violence: 0.01, suicide: 0.01, cardio: 0.50, cancer: 0.12, respiratory: 0.07, diabetes: 0.06, dementia: 0.05, other: 0.10 },
  },
  WPR: {
    '0-4': { neonatal: 0.52, infection: 0.22, injury: 0.09, other: 0.17 },
    '5-14': { infection: 0.10, injury: 0.28, traffic: 0.12, violence: 0.02, cardio: 0.06, cancer: 0.16, respiratory: 0.04, diabetes: 0.01, other: 0.21 },
    '15-29': { infection: 0.05, maternal: 0.01, injury: 0.20, traffic: 0.17, violence: 0.04, suicide: 0.18, cardio: 0.10, cancer: 0.10, respiratory: 0.02, diabetes: 0.01, other: 0.12 },
    '30-49': { infection: 0.05, maternal: 0.01, injury: 0.10, traffic: 0.08, violence: 0.02, suicide: 0.09, cardio: 0.25, cancer: 0.22, respiratory: 0.03, diabetes: 0.03, other: 0.12 },
    '50-69': { infection: 0.03, injury: 0.04, traffic: 0.02, violence: 0.01, suicide: 0.03, cardio: 0.34, cancer: 0.33, respiratory: 0.06, diabetes: 0.03, dementia: 0.01, other: 0.10 },
    '70+': { infection: 0.04, injury: 0.02, traffic: 0.01, violence: 0.01, suicide: 0.01, cardio: 0.42, cancer: 0.17, respiratory: 0.11, diabetes: 0.03, dementia: 0.08, other: 0.10 },
  },
};
